import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Skeleton, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { useDispatch, useSelector } from 'react-redux'

import HomeCardHeader from './HomeCardHeader'
import styles from './MyTasksCard.module.css'
import shellStyles from './HomeCardShell.module.css'
import { truncateWithEllipsis } from '../../../helpers/utils'
import { loginState } from '../../login/login.slice'
import DateRangePicker from '../../projects/components/DateRangePicker'
import {
    mainTaskUpdate,
    taskUpdateSupport,
} from '../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
} from '../../taskRefactored/tasks.service'
import { showActionNotification } from '../../../components/notification'

const { Text } = Typography

const TASK_TAB_KEY_MAP = {
    Upcoming: 'upcoming',
    Overdue: 'overdue',
    Completed: 'completed',
}

const PROJECT_BADGE_MAX_LEN = 12

const PROJECT_DOT_FALLBACK_COLORS = [
    '#60a5fa',
    '#a78bfa',
    '#34d399',
    '#f472b6',
    '#fb923c',
    '#22d3ee',
]

const hashStringToHueColor = (str) => {
    if (!str) return PROJECT_DOT_FALLBACK_COLORS[0]
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    return PROJECT_DOT_FALLBACK_COLORS[
        Math.abs(hash) % PROJECT_DOT_FALLBACK_COLORS.length
    ]
}

/** Resolve project id when API returns a string or a populated object. */
export const resolveTaskProjectId = (task) => {
    if (!task) return null
    const p = task.projectId
    if (p && typeof p === 'object' && (p._id || p.id)) {
        return String(p._id || p.id)
    }
    if (p != null && p !== '') return String(p)
    if (task.project?._id) return String(task.project._id)
    if (task.project?.id) return String(task.project.id)
    return null
}

const getTaskProjectName = (task) => {
    if (task?.taskSource === 'supportTask') return 'Support'
    return (
        task?.projectName ||
        task?.project?.name ||
        (typeof task?.projectId === 'object' ? task?.projectId?.name : '') ||
        task?.projectTitle ||
        task?.development?.projectName ||
        ''
    )
}

const getTaskProjectDotColor = (task) => {
    if (task?.taskSource === 'supportTask') return '#94a3b8'
    const explicit =
        task?.project?.colorCode ||
        task?.projectColorCode ||
        task?.colorCode ||
        task?.development?.colorCode
    if (explicit) return explicit
    return hashStringToHueColor(getTaskProjectName(task) || task?.title || '')
}

const TASK_ROW_SKELETON_COUNT = 6

const SKELETON_TITLE_WIDTHS_PCT = [88, 72, 94, 68, 82, 76]

const TaskRowSkeleton = ({ rowIndex, taskType }) => {
    const titleW =
        SKELETON_TITLE_WIDTHS_PCT[rowIndex % SKELETON_TITLE_WIDTHS_PCT.length]
    const twoLines = rowIndex % 3 === 0

    return (
        <div
            className={`${styles.taskRow} ${styles.taskRowSkeleton}`}
            aria-hidden
        >
            <div className={styles.taskRowStart}>
                {taskType === 'support' ? (
                    <div className={styles.skeletonTaskRingWrap}>
                        <Skeleton.Avatar active size={16} shape="circle" />
                    </div>
                ) : null}
                <div
                    className={styles.skeletonTaskTitleBlock}
                    style={{ maxWidth: `${titleW}%` }}
                >
                    <Skeleton.Input
                        active
                        size="small"
                        block
                        className={styles.skeletonTaskTitleLine}
                    />
                    {twoLines ? (
                        <div className={styles.skeletonTaskTitleSecondWrap}>
                            <Skeleton.Input
                                active
                                size="small"
                                block
                                className={styles.skeletonTaskTitleLineSecond}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            <div className={styles.taskRowEnd}>
                <Skeleton.Button
                    active
                    size="small"
                    className={styles.skeletonTaskProjectPill}
                />
                <Skeleton.Input
                    active
                    size="small"
                    className={styles.skeletonTaskDateSlot}
                />
            </div>
        </div>
    )
}

const MyTasksCard = ({
    taskTab,
    setTaskTab,
    tasksByTab,
    selectedTaskState,
    onRetry,
    onLoadMore,
    onOpenTask,
    onTaskDatesUpdated,
}) => {
    const dispatch = useDispatch()
    const scrollRef = useRef(null)
    const sentinelRef = useRef(null)

    const { userProfile } = useSelector(loginState)

    const handleDateRangeChange = useCallback(
        async (task, dateRange) => {
            const projectId = resolveTaskProjectId(task)
            if (!task?._id || !projectId) {
                showActionNotification({
                    type: 'error',
                    message: 'Cannot update dates: task has no project.',
                    notificationKey: 'home-task-date-missing-project',
                })
                return
            }
            try {
                let result
                if (task?.taskSource === 'supportTask') {
                    const payload = {
                        _id: task._id,
                        updateTask: {
                            title: task?.title || '',
                            projectId,
                            type: task?.type || 'Task',
                            assigneeId:
                                task?.assigneeId?.[0]?._id ??
                                task?.assigneeId?._id ??
                                null,
                            priority: task?.priority || 'Low',
                            status: task?.status || 'To Do',
                            description: task?.description,
                            start: dateRange?.start ?? null,
                            end: dateRange?.end ?? null,
                        },
                    }
                    result = await dispatch(taskUpdateSupport(payload))
                } else {
                    const body = buildMainTaskUpdateBodyFromTask(
                        task,
                        projectId,
                        {
                            start: dateRange?.start ?? null,
                            end: dateRange?.end ?? null,
                        }
                    )
                    result = await dispatch(
                        mainTaskUpdate({ id: task._id, body })
                    )
                }
                const data = result?.payload?.data
                const ok =
                    task?.taskSource === 'supportTask'
                        ? !!data?.success
                        : isTaskMutationSuccess(data)
                if (ok) {
                    onTaskDatesUpdated?.()
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            data?.message || 'Failed to update task dates',
                        notificationKey: 'home-task-date-update-error',
                    })
                }
            } catch {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update task dates',
                    notificationKey: 'home-task-date-update-error',
                })
            }
        },
        [dispatch, onTaskDatesUpdated]
    )

    const [taskType, setTaskType] = useState('development')
    const supportOnlyClient = Boolean(
        userProfile?.isSupportUser && !userProfile?.isExistingUser
    )
    const effectiveTaskType = supportOnlyClient ? 'support' : taskType

    useEffect(() => {
        if (supportOnlyClient) {
            setTaskType('support')
        }
    }, [supportOnlyClient])

    const selectedTaskTabKey = TASK_TAB_KEY_MAP[taskTab] || 'upcoming'
    const overdueTotal = tasksByTab?.overdue?.total || 0
    const items = useMemo(
        () => selectedTaskState?.items || [],
        [selectedTaskState?.items]
    )
    const filteredItems = useMemo(
        () =>
            items.filter((task) =>
                effectiveTaskType === 'support'
                    ? task?.taskSource === 'supportTask'
                    : task?.taskSource === 'task'
            ),
        [items, effectiveTaskType]
    )

    const options = useMemo(
        () => [
            { label: 'Upcoming', value: 'Upcoming' },
            { label: `Overdue (${overdueTotal})`, value: 'Overdue' },
            { label: 'Completed', value: 'Completed' },
        ],
        [overdueTotal]
    )

    const emptyTasksCopy = useMemo(() => {
        const tabKey = TASK_TAB_KEY_MAP[taskTab] || 'upcoming'
        const isSupport = effectiveTaskType === 'support'
        const trackWord = isSupport ? 'support' : 'development'

        if (tabKey === 'overdue') {
            return {
                toneClass: styles.emptyToneOverdue,
                icon: 'solar:alarm-bold-duotone',
                title: `No overdue ${trackWord} tasks`,
                hint: 'Anything past its due date will show up here so you can tackle it first.',
            }
        }
        if (tabKey === 'completed') {
            return {
                toneClass: styles.emptyToneCompleted,
                icon: 'solar:check-circle-bold-duotone',
                title: `No completed ${trackWord} tasks yet`,
                hint: 'Wrapped-up work appears here for a lightweight history at a glance.',
            }
        }
        return {
            toneClass: styles.emptyToneUpcoming,
            icon: 'solar:calendar-mark-bold-duotone',
            title: `No upcoming ${trackWord} tasks`,
            hint: 'When new work is scheduled for you, it lands in this list automatically.',
        }
    }, [taskTab, effectiveTaskType])
    useEffect(() => {
        if (
            !sentinelRef.current ||
            !scrollRef.current ||
            !selectedTaskState?.hasNext
        ) {
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) onLoadMore?.(selectedTaskTabKey)
            },
            {
                root: scrollRef.current,
                rootMargin: '140px 0px',
                threshold: 0.1,
            }
        )
        observer.observe(sentinelRef.current)
        return () => observer.disconnect()
    }, [
        selectedTaskState?.hasNext,
        selectedTaskTabKey,
        onLoadMore,
        items.length,
    ])

    return (
        <Card
            className={`${shellStyles.cardBase} ${styles.tasksCard}`.trim()}
            bordered={false}
        >
            <HomeCardHeader
                variant="tasks"
                userProfile={userProfile}
                taskTab={taskTab}
                onTaskTabChange={setTaskTab}
                tabOptions={options}
                taskType={effectiveTaskType}
                onTaskTypeChange={setTaskType}
                showTaskTypeDropdown={
                    !!userProfile &&
                    (userProfile?.isSuperAdmin ||
                        userProfile?.isExistingUser)
                }
            />
            <div className={shellStyles.cardBody}>
                <div className={shellStyles.scrollArea} ref={scrollRef}>
                    <div className={styles.taskList}>
                        {selectedTaskState?.status === 'loading'
                            ? Array.from(
                                  { length: TASK_ROW_SKELETON_COUNT },
                                  (_, rowIndex) => (
                                      <TaskRowSkeleton
                                          key={`task-skeleton-${rowIndex}`}
                                          rowIndex={rowIndex}
                                          taskType={effectiveTaskType}
                                      />
                                  )
                              )
                            : filteredItems.map((task, idx) => {
                                  const title =
                                      task?.title || 'Untitled task'
                                  const projectName = getTaskProjectName(task)
                                  const projectBadgeText = projectName
                                      ? truncateWithEllipsis(
                                            projectName,
                                            PROJECT_BADGE_MAX_LEN
                                        )
                                      : ''
                                  const dotColor = getTaskProjectDotColor(task)
                                  const isTaskCompleted =
                                      selectedTaskTabKey === 'completed' ||
                                      Boolean(
                                          task?.isCompleted ?? task?.isComplete
                                      )

                                  return (
                                      <div
                                          key={
                                              task?._id ||
                                              `${task?.taskKey || 'task'}-${idx}`
                                          }
                                          className={styles.taskRow}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() =>
                                              onOpenTask?.(task)
                                          }
                                          onKeyDown={(e) => {
                                              if (
                                                  e.key === 'Enter' ||
                                                  e.key === ' '
                                              ) {
                                                  e.preventDefault()
                                                  onOpenTask?.(task)
                                              }
                                          }}
                                          aria-label={`Open task: ${title}`}
                                      >
                                          <div
                                              className={styles.taskRowStart}
                                          >
                                              {effectiveTaskType === 'support' ? (
                                                  <span
                                                      className={`${styles.taskStatusRing} ${
                                                          isTaskCompleted
                                                              ? styles.taskStatusRingCompleted
                                                              : ''
                                                      }`}
                                                      aria-hidden
                                                  >
                                                      <Icon
                                                          icon="iconamoon:check-light"
                                                          width={13}
                                                          height={13}
                                                          className={
                                                              styles.taskStatusCheck
                                                          }
                                                      />
                                                  </span>
                                              ) : null}
                                              <Text
                                                  className={styles.taskTitle}
                                              >
                                                  {title}
                                              </Text>
                                          </div>
                                          <div
                                              className={styles.taskRowEnd}
                                          >
                                              {projectBadgeText ? (
                                                  <span
                                                      className={
                                                          styles.taskProjectPill
                                                      }
                                                      title={projectName}
                                                  >
                                                      <span
                                                          className={
                                                              styles.taskProjectDot
                                                          }
                                                          style={{
                                                              backgroundColor:
                                                                  dotColor,
                                                          }}
                                                          aria-hidden
                                                      />
                                                      <span
                                                          className={
                                                              styles.taskProjectPillText
                                                          }
                                                      >
                                                          {projectBadgeText}
                                                      </span>
                                                  </span>
                                              ) : null}
                                              <div
                                                  className={
                                                      styles.taskDatePickerWrap
                                                  }
                                                  onClick={(e) =>
                                                      e.stopPropagation()
                                                  }
                                                  onKeyDown={(e) =>
                                                      e.stopPropagation()
                                                  }
                                              >
                                                  <DateRangePicker
                                                      value={{
                                                          start:
                                                              task?.start ||
                                                              null,
                                                          end:
                                                              task?.end || null,
                                                      }}
                                                      onChange={(dateRange) =>
                                                          handleDateRangeChange(
                                                              task,
                                                              dateRange
                                                          )
                                                      }
                                                      placeholder=""
                                                      parentComponent="taskListView"
                                                  />
                                              </div>
                                          </div>
                                      </div>
                                  )
                              })}
                    </div>
                    {filteredItems.length > 0 &&
                    selectedTaskState?.hasNext &&
                    selectedTaskState?.status !== 'error' ? (
                        <div className={styles.taskShowMoreWrap}>
                            <button
                                type="button"
                                className={styles.taskShowMore}
                                disabled={
                                    selectedTaskState?.status === 'loadingMore'
                                }
                                onClick={() => onLoadMore?.(selectedTaskTabKey)}
                            >
                                {selectedTaskState?.status === 'loadingMore'
                                    ? 'Loading...'
                                    : 'Show more'}
                            </button>
                        </div>
                    ) : null}
                    {selectedTaskState?.status === 'error' ? (
                        <div className={shellStyles.errorWrap}>
                            <Text className={shellStyles.errorText}>
                                {selectedTaskState?.error}
                            </Text>
                            <button
                                type="button"
                                className={shellStyles.retryBtn}
                                onClick={onRetry}
                            >
                                Retry
                            </button>
                        </div>
                    ) : null}
                    {selectedTaskState?.status !== 'loading' &&
                    filteredItems.length === 0 &&
                    selectedTaskState?.status !== 'error' ? (
                        <div
                            className={`${styles.emptyTasksWrap} ${emptyTasksCopy.toneClass}`}
                            role="status"
                        >
                            <div className={styles.emptyTasksIconCircle}>
                                <Icon
                                    icon={emptyTasksCopy.icon}
                                    width={36}
                                    height={36}
                                    aria-hidden
                                />
                            </div>
                            <Text className={styles.emptyTasksTitle}>
                                {emptyTasksCopy.title}
                            </Text>
                            <Text className={styles.emptyTasksHint}>
                                {emptyTasksCopy.hint}
                            </Text>
                        </div>
                    ) : null}
                    {selectedTaskState?.status === 'loadingMore' ? (
                        <Text className={shellStyles.stateText}>
                            Loading more...
                        </Text>
                    ) : null}
                    {selectedTaskState?.hasNext &&
                    selectedTaskState?.status !== 'error' ? (
                        <div
                            ref={sentinelRef}
                            className={shellStyles.sentinel}
                            aria-hidden="true"
                        />
                    ) : null}
                </div>
            </div>
        </Card>
    )
}

export default MyTasksCard
